import { Fragment, useEffect, useState } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { CheckboxItem } from '@radix-ui/react-dropdown-menu';

import { FileFormat } from '@/types/api-types';

import IconChevronDown from '@/assets/svg/icon-chevron-down.svg?react';
import IconClose from '@/assets/svg/icon-close.svg?react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InputDays } from '@/components/ui/input-days';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useAuthStore } from '@/stores/authStore';
import { useGlobalStore } from '@/stores/globalStore';

import { RESOLUTION_OPTIONS, USER_ROLES } from '@/constants/global-constants';

export function InputFileValidationSettings() {
  const [isSaving, setIsSaving] = useState(false);

  const { user } = useAuthStore();
  const { fileValidationSettings, updateFileValidationSettings } =
    useGlobalStore();
  const [canBeEdited, setCanBeEdited] = useState(false);

  const [formats, setFormats] = useState<FileFormat[]>([]);
  const [open, setOpen] = useState(false);
  const [checkMetadata, setCheckMetadata] = useState(false);
  const [minResolution, setMinResolution] = useState<string>('');
  const [maxResolution, setMaxResolution] = useState<string>('');
  const [shootingDate, setShootingDate] = useState<string>('');
  const [uploadDate, setUploadDate] = useState<string>('');

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      if (fileValidationSettings.length > 0) {
        const firstSetting = fileValidationSettings[0];
        setFormats(firstSetting.allowed_formats);
        setCheckMetadata(firstSetting.check_metadata);
        setMinResolution(firstSetting.min_resolution || '');
        setMaxResolution(firstSetting.max_resolution || '');
        setShootingDate(firstSetting.max_shooting_date_days?.toString() || '');
        setUploadDate(firstSetting.max_upload_date_days?.toString() || '');
      }
    };
    loadSettings();
  }, [fileValidationSettings]);

  const handleFormatChange = (formatId: string, checked: boolean) => {
    setFormats((prev) =>
      prev.map((format) =>
        format.id === formatId ? { ...format, enabled: checked } : format,
      ),
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    if (
      !user ||
      user.role !== USER_ROLES.ADMIN.value ||
      !fileValidationSettings
    ) {
      setIsSaving(false);
      return;
    }

    await updateFileValidationSettings(fileValidationSettings[0].uuid, {
      allowed_formats: formats,
      check_metadata: checkMetadata,
      min_resolution: minResolution || null,
      max_resolution: maxResolution || null,
      max_shooting_date_days: shootingDate ? parseInt(shootingDate) : null,
      max_upload_date_days: uploadDate ? parseInt(uploadDate) : null,
    });

    setCanBeEdited(false);
    setIsSaving(false);
  };

  const isAdmin = user?.role === USER_ROLES.ADMIN.value;

  return (
    <Card className="rounded-md bg-white p-5 shadow-sm sm:w-[490px]">
      <div className="mb-5">
        <h2 className="mb-2.5 text-2xl font-semibold text-primary">
          Налаштування валідації файлів
        </h2>
        <p className="text-base font-normal leading-5 text-primary">
          Даний блок включає налаштування валідації файлів, що завантажують
          користувачі системи.
        </p>
      </div>

      <div className="space-y-5">
        <div className="relative">
          <label className="absolute -top-2 left-4 z-30 bg-white px-1 text-xs font-medium text-gray-200">
            Формати файлів
          </label>
          <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <div className="relative flex min-h-[50px] w-full flex-1 flex-wrap items-start gap-2 rounded-md border border-gray-200 p-4 text-left">
              <div className="flex w-full flex-wrap gap-2 pr-8">
                {formats.filter((f) => f.enabled).length > 0 ? (
                  formats
                    .filter((f) => f.enabled)
                    .map((format) => (
                      <span
                        key={format.id}
                        className="relative z-0 inline-flex items-center gap-1 rounded-sm border border-separator px-1 py-0.5 text-sm"
                      >
                        {format.label}
                        <button
                          onClick={() => handleFormatChange(format.id, false)}
                          className="relative z-10 rounded-sm hover:bg-blue-500 hover:[&>svg]:fill-white"
                          disabled={!canBeEdited}
                        >
                          <IconClose className="z-0 min-h-3 min-w-3 fill-blue-500 p-0.5 transition-colors" />
                        </button>
                      </span>
                    ))
                ) : (
                  <span className="text-base text-gray-200">
                    Оберіть формати
                  </span>
                )}
              </div>

              <DropdownMenuTrigger asChild disabled={!canBeEdited}>
                <button className="absolute right-4 top-1/2 -translate-y-1/2 focus-visible:outline-none">
                  <IconChevronDown className="h-[10px] w-[20px] fill-transparent stroke-gray-200" />
                </button>
              </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent className="w-full rounded-md border border-gray-200 bg-white shadow-sm sm:w-[450px]">
              {formats.map((format, index) => (
                <Fragment key={format.id}>
                  <CheckboxItem
                    className="font-montserrat relative flex h-[50px] cursor-pointer items-center px-[37px] text-base text-primary outline-none hover:bg-gray-50 focus:bg-gray-50"
                    checked={format.enabled}
                    onCheckedChange={(checked) =>
                      handleFormatChange(format.id, checked as boolean)
                    }
                  >
                    <div
                      className={`absolute left-[18px] flex h-[10px] w-[10px] items-center justify-center rounded-[2px] border border-primary ${
                        format.enabled ? 'bg-blue-500' : 'bg-transparent'
                      }`}
                    ></div>
                    {format.label}
                  </CheckboxItem>
                  {index < formats.length - 1 && (
                    <div className="h-[1px] w-full bg-separator" />
                  )}
                </Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="font-montserrat text-base leading-none text-primary">
              Перевірка на наявність метаданих
            </div>
            <div className="font-montserrat text-xs text-primary">
              (тільки для зображень)
            </div>
          </div>
          <Checkbox.Root
            className="flex h-[50px] w-[50px] items-center justify-center rounded-md border border-gray-200"
            checked={checkMetadata}
            onCheckedChange={(checked) => setCheckMetadata(checked as boolean)}
            disabled={!canBeEdited}
          >
            <Checkbox.Indicator>
              <IconClose className="h-[20px] w-[20px] fill-primary" />
            </Checkbox.Indicator>
          </Checkbox.Root>
        </div>

        <div className="relative">
          <label className="absolute -top-2 left-4 z-30 bg-white px-1 text-xs font-medium text-gray-200">
            Роздільна здатність (не менше)
          </label>
          <Select
            disabled={!canBeEdited}
            value={minResolution}
            onValueChange={setMinResolution}
          >
            <SelectTrigger>
              <SelectValue placeholder="Оберіть роздільну здатність" />
            </SelectTrigger>
            <SelectContent>
              {RESOLUTION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span>{option.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <label className="absolute -top-2 left-4 z-30 bg-white px-1 text-xs font-medium text-gray-200">
            Роздільна здатність (не більше)
          </label>
          <Select
            disabled={!canBeEdited}
            value={maxResolution}
            onValueChange={setMaxResolution}
          >
            <SelectTrigger>
              <SelectValue placeholder="Оберіть роздільну здатність" />
            </SelectTrigger>
            <SelectContent>
              {RESOLUTION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <InputDays
            label="Дата зйомки"
            disabled={!canBeEdited}
            value={shootingDate}
            onChange={(e) => setShootingDate(e.target.value)}
            placeholder="Введіть кількість днів"
          />
          <InputDays
            label="Дата завантаження"
            disabled={!canBeEdited}
            value={uploadDate}
            onChange={(e) => setUploadDate(e.target.value)}
            placeholder="Введіть кількість днів"
          />
        </div>
      </div>

      <div className="mt-[30px] flex justify-between space-x-4">
        <Button
          variant={canBeEdited ? 'destructive' : 'default'}
          disabled={!canBeEdited || !isAdmin || isSaving}
          onClick={handleSave}
        >
          {isSaving ? 'Збереження...' : 'Зберегти'}
        </Button>
        <Button
          variant="default"
          onClick={() => setCanBeEdited((prev) => !prev)}
          disabled={!isAdmin}
        >
          {canBeEdited ? 'Скасувати' : 'Редагувати'}
        </Button>
      </div>
    </Card>
  );
}
